import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from './language-context';
import { useSignedInAccount } from './signed-in-account';
import { BrandLockup } from './brand-lockup';
import type { NavMenuItem } from './nav-menu';
import { trapTabKey } from './trap-tab-key';

// The website nav drawer (design DC:224-255; CB-17 `nav-drawer.tsx`). Opened by the ☰
// in the compact 768–1149 px header, where the centre nav does not fit. A right-hand
// panel `min(340px, 88vw)` over a scrim, holding the same destinations as the desktop
// centre nav: Offers and Booking, each with its category and popular rows, Videos, the resources,
// sign in, and the "add your business" CTA pinned to the bottom.
//
// Real links throughout, so every row is keyboard-reachable and middle-clickable. The
// drawer is a modal dialog: Escape and the scrim close it, the close button takes
// focus on open, focus returns to whatever opened it, and the page behind does not
// scroll.

export function NavDrawer({
  open,
  onClose,
  categoryItems,
  popularItems,
  bookingCategoryItems,
  bookingPopularItems,
  resourceItems,
}: {
  open: boolean;
  onClose: () => void;
  categoryItems: NavMenuItem[];
  popularItems: NavMenuItem[];
  bookingCategoryItems: NavMenuItem[];
  bookingPopularItems: NavMenuItem[];
  resourceItems: NavMenuItem[];
}) {
  const { t } = useLanguage();
  const { user } = useSignedInAccount();
  // Callers often pass an inline closer; reading it through a ref keeps the open
  // effect (focus, scroll lock) from re-running — and stealing focus — on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      else trapTabKey(e, panelRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="navdrawer">
      <div className="navdrawer__backdrop" aria-hidden="true" onClick={onClose} />
      <div ref={panelRef} className="navdrawer__panel" role="dialog" aria-modal="true" aria-label={t('nav.menu')}>
        <div className="navdrawer__head">
          <BrandLockup className="navdrawer__brand" />
          <button ref={closeRef} type="button" className="navdrawer__close" aria-label={t('shell.close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <nav className="navdrawer__body" aria-label={t('nav.primary')}>
          <Link className="navdrawer__row navdrawer__row--offers navdrawer__row--group" to="/offer" onClick={onClose}>
            {t('nav.offers')}
            <span className="navdrawer__badge" aria-hidden="true">
              %
            </span>
          </Link>
          <div className="navdrawer__sub">
            {categoryItems.map((c) => (
              <Link key={c.to} className="navdrawer__subrow" to={c.to} onClick={onClose}>
                {c.label}
              </Link>
            ))}
            <div className="navdrawer__grouptitle">{t('nav.popular')}</div>
            {popularItems.map((p) => (
              <Link key={p.to + p.label} className="navdrawer__subrow" to={p.to} onClick={onClose}>
                {p.label}
                {p.badge ? (
                  <span className="navdrawer__new" aria-hidden="true">
                    {p.badge}
                  </span>
                ) : null}
              </Link>
            ))}
          </div>
          <Link className="navdrawer__row navdrawer__row--group" to="/search" onClick={onClose}>
            {t('nav.booking')}
          </Link>
          <div className="navdrawer__sub">
            {bookingCategoryItems.map((c) => (
              <Link key={c.to} className="navdrawer__subrow" to={c.to} onClick={onClose}>
                {c.label}
              </Link>
            ))}
            <div className="navdrawer__grouptitle">{t('nav.popular')}</div>
            {bookingPopularItems.map((p) => (
              <Link key={p.to + p.label} className="navdrawer__subrow" to={p.to} onClick={onClose}>
                {p.label}
              </Link>
            ))}
          </div>
          <Link className="navdrawer__row" to="/videos" onClick={onClose}>
            {t('nav.videos')}
            <span className="navdrawer__new" aria-hidden="true">
              {t('nav.new_badge')}
            </span>
          </Link>
          {resourceItems.map((r) => (
            <Link key={r.to + r.label} className="navdrawer__row" to={r.to} onClick={onClose}>
              {r.label}
            </Link>
          ))}
          {!user && (
            <Link className="navdrawer__row navdrawer__row--signin" to="/login" onClick={onClose}>
              {t('nav.sign_in')}
            </Link>
          )}
        </nav>
        <div className="navdrawer__foot">
          <Link className="navdrawer__cta" to="/for-business" onClick={onClose}>
            {t('nav.add_business_free')}
          </Link>
        </div>
      </div>
    </div>
  );
}
